import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { buildAccountOptions } from '../lib/buildAccountOptions';
import {
  buildExplorationSearchParams,
  resolveExplorationUrlSnapshot,
  snapshotFromExplorationState,
  snapshotsEqual,
  type ExplorationUrlSnapshot,
} from '../lib/explorationUrlParams';
import { resolveUrlVolumeHydration } from '../lib/explorationUrlVolumeHydration';
import { useExplorationData, useExplorationDispatch, useExplorationState } from '../context/ExplorationContext';
import { planExplorationUrlPush, shouldReplaceExplorationUrlPush } from './explorationUrlSyncPlan';

const SEARCH_URL_DEBOUNCE_MS = 300;

export function useExplorationUrlSync() {
  const state = useExplorationState();
  const dispatch = useExplorationDispatch();
  const { accounts } = useExplorationData();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastPushedParamsRef = useRef<string | null>(null);
  const hydratedRef = useRef(false);
  const skipPushRef = useRef(false);
  const pendingVolumeHydrationRef = useRef<string | null>(null);
  const prevAccountIdRef = useRef(state.accountId);
  const prevPushedSnapshotRef = useRef<ExplorationUrlSnapshot | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestSnapshotRef = useRef<ExplorationUrlSnapshot | null>(null);

  const accountOptions = useMemo(() => buildAccountOptions(accounts), [accounts]);
  const defaultAccountId = accountOptions[0]?.id ?? null;

  const selectedVolumeId =
    state.selectedNode?.kind === 'volume' ? state.selectedNode.volume.ebsId : null;
  const selectedNodeRef = useRef(state.selectedNode);
  selectedNodeRef.current = state.selectedNode;

  const stateSnapshot = useMemo(
    () => snapshotFromExplorationState(state),
    [
      state.accountId,
      state.view,
      state.displayMode,
      state.secondary,
      state.coverageGrouping,
      state.tagKey,
      state.drillPath,
      state.searchQuery,
      state.filters,
      state.groupByAccount,
      state.selectedNode,
    ]
  );

  latestSnapshotRef.current = stateSnapshot;

  const pushSnapshotToUrl = useCallback(
    (snapshot: ExplorationUrlSnapshot, replace: boolean) => {
      setSearchParams(
        (prev) => {
          const nextParams = buildExplorationSearchParams(prev, snapshot, {
            defaultAccountId,
          });
          const nextParamsString = nextParams.toString();
          if (nextParamsString === prev.toString()) {
            return prev;
          }

          lastPushedParamsRef.current = nextParamsString;
          prevPushedSnapshotRef.current = snapshot;
          return nextParams;
        },
        { replace }
      );
    },
    [defaultAccountId, setSearchParams]
  );

  const clearSearchDebounce = useCallback(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  }, []);

  const hydrateVolumeFromUrl = useCallback(
    (snapshot: ExplorationUrlSnapshot) => {
      const result = resolveUrlVolumeHydration(
        accounts,
        snapshot.accountId ?? state.accountId,
        snapshot.selectedVolumeId,
        selectedVolumeId
      );

      switch (result.status) {
        case 'none':
        case 'already_selected':
          pendingVolumeHydrationRef.current = null;
          break;
        case 'pending':
          pendingVolumeHydrationRef.current = result.volumeId;
          break;
        case 'ready':
          pendingVolumeHydrationRef.current = null;
          dispatch({ type: 'SELECT_VOLUME', volume: result.volume, historyMode: 'none' });
          break;
      }
    },
    [accounts, dispatch, selectedVolumeId, state.accountId]
  );

  useEffect(() => {
    const prevAccountId = prevAccountIdRef.current;
    prevAccountIdRef.current = state.accountId;
    if (
      prevAccountId != null &&
      state.accountId != null &&
      prevAccountId !== state.accountId
    ) {
      skipPushRef.current = false;
      pendingVolumeHydrationRef.current = resolveExplorationUrlSnapshot(
        searchParams,
        accounts
      ).selectedVolumeId;
    }
  }, [accounts, searchParams, state.accountId]);

  useEffect(() => {
    const parsed = resolveExplorationUrlSnapshot(searchParams, accounts);
    const hasAccountContext = Boolean(parsed.accountId ?? state.accountId);
    if (!hasAccountContext) return;

    const currentParams = searchParams.toString();

    if (!hydratedRef.current) {
      lastPushedParamsRef.current = currentParams;
      hydratedRef.current = true;
      skipPushRef.current = true;
      if (!snapshotsEqual(parsed, stateSnapshot)) {
        dispatch({ type: 'SYNC_FROM_URL', snapshot: parsed });
      }
      prevPushedSnapshotRef.current = parsed;
      hydrateVolumeFromUrl(parsed);
      return;
    }

    if (currentParams === lastPushedParamsRef.current) return;

    lastPushedParamsRef.current = currentParams;
    prevPushedSnapshotRef.current = parsed;
    skipPushRef.current = true;
    if (!snapshotsEqual(parsed, stateSnapshot)) {
      dispatch({ type: 'SYNC_FROM_URL', snapshot: parsed });
    }
    hydrateVolumeFromUrl(parsed);
  }, [accounts, dispatch, hydrateVolumeFromUrl, searchParams, state.accountId, stateSnapshot]);

  useEffect(() => {
    const hasAccountContext = Boolean(stateSnapshot.accountId ?? state.accountId);
    if (!hasAccountContext || !hydratedRef.current) return;

    if (skipPushRef.current) {
      skipPushRef.current = false;
      // Explicit navigation writes must still apply — otherwise volume= pushes are
      // dropped and Back can only restore instance-level URLs.
      if (state.urlHistoryWrite == null) {
        // Do not overwrite prevPushedSnapshotRef — params effect already set it to the
        // URL snapshot. Replacing with a stale stateSnapshot caused heuristic re-pushes
        // after Back+hydrate (duplicate same-URL entries).
        return;
      }
    }

    const prev = prevPushedSnapshotRef.current ?? stateSnapshot;
    const pushPlan = planExplorationUrlPush(
      prev,
      stateSnapshot,
      state.urlHistoryWrite
    );
    if (pushPlan === 'none') {
      if (state.urlHistoryWrite != null) {
        // Align baseline after volume hydrate so the next heuristic pass does not
        // treat restored selection as a new navigation. Instance selection-only
        // leaves volume= on the URL — keep the prior URL baseline in that case.
        if (state.urlHistoryWrite === 'none' && selectedVolumeId != null) {
          prevPushedSnapshotRef.current = stateSnapshot;
        }
        dispatch({ type: 'CLEAR_URL_HISTORY_WRITE' });
      }
      return;
    }

    if (pushPlan === 'debounced-replace') {
      clearSearchDebounce();
      searchDebounceRef.current = setTimeout(() => {
        searchDebounceRef.current = null;
        const snapshot = latestSnapshotRef.current;
        if (!snapshot) return;
        const replace = shouldReplaceExplorationUrlPush('debounced-replace');
        if (replace == null) return;
        pushSnapshotToUrl(snapshot, replace);
        if (state.urlHistoryWrite != null) {
          dispatch({ type: 'CLEAR_URL_HISTORY_WRITE' });
        }
      }, SEARCH_URL_DEBOUNCE_MS);
      return;
    }

    clearSearchDebounce();
    const replace = shouldReplaceExplorationUrlPush(pushPlan);
    if (replace == null) return;
    pushSnapshotToUrl(stateSnapshot, replace);
    if (state.urlHistoryWrite != null) {
      dispatch({ type: 'CLEAR_URL_HISTORY_WRITE' });
    }
  }, [
    clearSearchDebounce,
    dispatch,
    pushSnapshotToUrl,
    selectedVolumeId,
    state.accountId,
    state.urlHistoryWrite,
    stateSnapshot,
  ]);

  useEffect(() => {
    return () => {
      if (!searchDebounceRef.current) return;

      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;

      const snapshot = latestSnapshotRef.current;
      if (!snapshot || !hydratedRef.current) return;

      const prev = prevPushedSnapshotRef.current ?? snapshot;
      const pushPlan = planExplorationUrlPush(prev, snapshot, null);
      const replace = shouldReplaceExplorationUrlPush(pushPlan);
      if (replace == null) return;

      pushSnapshotToUrl(snapshot, replace);
    };
  }, [pushSnapshotToUrl]);

  useEffect(() => {
    const hasAccountContext = Boolean(stateSnapshot.accountId ?? state.accountId);
    if (!hasAccountContext || !hydratedRef.current) return;

    const pendingId = pendingVolumeHydrationRef.current;
    if (!pendingId) return;

    const result = resolveUrlVolumeHydration(
      accounts,
      state.accountId ?? stateSnapshot.accountId,
      pendingId,
      selectedVolumeId
    );

    if (result.status === 'already_selected') {
      pendingVolumeHydrationRef.current = null;
      return;
    }

    if (selectedNodeRef.current != null && selectedVolumeId !== pendingId) {
      pendingVolumeHydrationRef.current = null;
      return;
    }

    if (result.status !== 'ready') return;

    pendingVolumeHydrationRef.current = null;
    dispatch({ type: 'SELECT_VOLUME', volume: result.volume, historyMode: 'none' });
  }, [
    accounts,
    dispatch,
    selectedVolumeId,
    state.accountId,
    state.selectedNode,
    stateSnapshot.accountId,
  ]);
}
